import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUpload from "@/components/ImageUpload";

const EMPTY = { category: "lol", role: "", nickname: "", name: "", detail: "", image_url: "", opgg_url: "", operators: "", rank: "" };
const inputCls = "w-full rounded-lg border border-input bg-[#060B18] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";

export default function RostersTab() {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/rosters").then((r) => setMembers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/rosters/${editing}`, form);
        toast.success("Membre mis à jour");
      } else {
        await api.post("/admin/rosters", form);
        toast.success("Membre ajouté");
      }
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/rosters/${id}`);
      toast.success("Membre supprimé");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const catLabel = { lol: "LoL", tft: "TFT", r6: "R6", staff: "Staff" };
  const isR6 = form.category === "r6";

  return (
    <div className="grid gap-6" data-testid="rosters-admin">
      <form onSubmit={submit} className="card-tactical grid gap-4 p-5" data-testid="admin-roster-form">
        <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
          {editing ? <Pencil className="h-5 w-5 text-orange-500" /> : <Plus className="h-5 w-5 text-orange-500" />}
          {editing ? "Modifier le membre" : "Ajouter un membre"}
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="admin-roster-category" className="bg-[#060B18]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="lol">League of Legends</SelectItem>
              <SelectItem value="tft">Teamfight Tactics</SelectItem>
              <SelectItem value="r6">Rainbow Six Siege</SelectItem>
              <SelectItem value="staff">Staff</SelectItem>
            </SelectContent>
          </Select>
          <input placeholder={isR6 ? "Rôle (ex: Entry Fragger, Support, IGL)" : "Rôle (ex: Top, Head Coach)"} value={form.role} data-testid="admin-roster-role"
            onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} required />
          <input placeholder="Pseudo" value={form.nickname} data-testid="admin-roster-nickname"
            onChange={(e) => setForm({ ...form, nickname: e.target.value })} className={inputCls} required />
          <input placeholder="Nom réel" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} required />
          {isR6 && (
            <>
              <input placeholder="Rank (ex: Champion, Diamant, Émeraude)" value={form.rank} data-testid="admin-roster-rank"
                onChange={(e) => setForm({ ...form, rank: e.target.value })} className={inputCls} />
              <input placeholder="Opérateurs favoris (ex: Ash, Thermite, Valkyrie)" value={form.operators} data-testid="admin-roster-operators"
                onChange={(e) => setForm({ ...form, operators: e.target.value })} className={inputCls} />
            </>
          )}
          <input placeholder={isR6 ? "Détail (expérience, palmarès, équipe précédente)" : "Détail (champions, rank, expérience)"} value={form.detail}
            onChange={(e) => setForm({ ...form, detail: e.target.value })} className={inputCls} />
          <div>
            <input placeholder="URL photo (optionnel)" value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={inputCls} />
            <div className="mt-2">
              <ImageUpload testid="admin-roster-image-upload" onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))} />
            </div>
          </div>
          <input placeholder={isR6 ? "Lien R6 Tracker du joueur (optionnel)" : "Lien OP.GG du joueur (optionnel)"} value={form.opgg_url} data-testid="admin-roster-opgg"
            onChange={(e) => setForm({ ...form, opgg_url: e.target.value })} className={inputCls} />
        </div>
        <div className="flex gap-3">
          <button type="submit" data-testid="admin-roster-submit"
            className="rounded-lg bg-orange-500 px-5 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
            {editing ? "Enregistrer" : "Ajouter"}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(EMPTY); }} data-testid="admin-roster-cancel"
              className="rounded-lg border border-slate-600 px-5 py-2.5 text-sm font-bold uppercase text-slate-300">
              Annuler
            </button>
          )}
        </div>
      </form>

      <div className="grid gap-3">
        {members.map((m) => (
          <div key={m.id} data-testid={`admin-roster-row-${m.nickname}`} className="card-tactical flex flex-wrap items-center justify-between gap-3 p-4">
            <div>
              <span className="mr-2 rounded border border-orange-500/40 bg-orange-500/10 px-2 py-0.5 text-[11px] font-bold uppercase text-orange-400">{catLabel[m.category]}</span>
              <span className="font-heading text-lg font-bold uppercase">{m.nickname}</span>
              <span className="ml-2 text-sm text-slate-400">{m.role} — {m.name}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setEditing(m.id); setForm({ category: m.category, role: m.role, nickname: m.nickname, name: m.name, detail: m.detail, image_url: m.image_url, opgg_url: m.opgg_url || "", operators: m.operators || "", rank: m.rank || "" }); }}
                data-testid={`admin-roster-edit-${m.nickname}`}
                className="rounded-lg border border-orange-500/40 p-2 text-orange-400 hover:bg-orange-500/10"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(m.id)} data-testid={`admin-roster-delete-${m.nickname}`}
                className="rounded-lg border border-red-500/40 p-2 text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
